<script setup lang="ts">
import { useFormattedDate } from '../composables/useFormattedDate'
import { useNow } from '../composables/useNow'
import type { CvGenerationRun } from '../stores/cv_generation_runs'

// What generation is doing, shown at the top of the Versions card: the run
// in flight, if any, and the last few failed runs with their reasons. The
// Generate button itself lives in the page header (CvHeaderActions).
defineProps<{
  active: CvGenerationRun | null
  failed: CvGenerationRun[]
}>()

const { formatDate, formatAbsoluteDate } = useFormattedDate()
const now = useNow()

function since(run: CvGenerationRun): string {
  const at = run.started_at ?? run.created_at
  return `${run.status === 'running' ? 'running' : 'queued'} since ${formatDate(at, now.value)}`
}
</script>

<template>
  <div class="flex flex-col gap-1.5" data-testid="cv-generation-panel">
    <p v-if="active" class="m-0 flex items-center gap-2 text-xs text-life" data-testid="cv-run-active">
      <span class="h-1.5 w-1.5 shrink-0 animate-pulse rounded-full bg-life" aria-hidden="true" />
      Run {{ active.id }} {{ since(active) }}
    </p>
    <ul v-if="failed.length" class="m-0 flex list-none flex-col gap-1 p-0" data-testid="cv-runs-failed">
      <li
        v-for="run in failed"
        :key="run.id"
        class="text-xs break-words text-status-alert"
        :title="run.finished_at ? formatAbsoluteDate(run.finished_at) : undefined"
        :data-testid="`cv-run-failed-${run.id}`"
      >
        Run {{ run.id }} failed<span v-if="run.finished_at"> {{ formatDate(run.finished_at, now) }}</span
        >: {{ run.error }}
      </li>
    </ul>
  </div>
</template>
